import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Button, ModifyGuestAlertsModal } from '..';
import { IconPeopleTwo } from '../../assets/icons';
import { ImageModifyGuestWithNoPet, ImageModifyGuestWithPet } from '../../assets/images';
import { AppRoutes } from '../../routes';
import { HomeInfo, Reservation, Stay, Guests } from 'guest-data-types-ts-lib';
import { TranslationUtils, ReservationUtils } from '../../utils';

import './ModifyGuestPanel.scss';

interface ModifyGuestPanelProps {
    home_info: HomeInfo;
    guests: Guests;
    stay: Stay;
    reservation: Reservation;
    localPhoneNumber: string;
}

export enum ModifyDeclineReasons {
    TRIP_ALREADY_STARTED = 'TRIP_ALREADY_STARTED',
    CREATED_BEFORE_JUNE_2018 = 'CREATED_BEFORE_JUNE_2018',
    SOURCE_NOT_ALLOWED = 'SOURCE_NOT_ALLOWED',
    REFUND_IN_PROGRESS = 'REFUND_IN_PROGRESS',
}

export const ModifyGuestPanel: React.FC<ModifyGuestPanelProps> = (props) => {
    const { guests, stay, reservation, home_info, localPhoneNumber } = props;
    const history = useHistory();
    const [isShowingModifyGuestAlertsModal, setIsShowingModifyGuestAlertsModal] = useState(false);
    const [occupancyStatus, setOccupancyStatus] = useState<{ isEligible: boolean; declineReason: ModifyDeclineReasons | null }>({
        isEligible: true,
        declineReason: null,
    });
    const negativeBalance = reservation.balance < 0;
    const { current_counts, max_counts } = guests;
    const currentGuests = current_counts.adults + current_counts.children;
    const allowsPets = home_info.allows_dogs || false;

    useEffect(() => {
        if (reservation && stay) {
            setEligibilityStatus();
        }
    }, []);

    const setEligibilityStatus = () => {
        let isEligible = true;
        let declineReason = null;
        if (ReservationUtils.isTripStarted(stay)) {
            isEligible = false;
            declineReason = ModifyDeclineReasons.TRIP_ALREADY_STARTED;
        } else if (!ReservationUtils.isSourceIdAllowed(reservation)) {
            isEligible = false;
            declineReason = ModifyDeclineReasons.SOURCE_NOT_ALLOWED;
        } else if (reservation.creation_date && ReservationUtils.isDateBeforeJuneFourth(new Date(reservation.creation_date))) {
            isEligible = false;
            declineReason = ModifyDeclineReasons.CREATED_BEFORE_JUNE_2018;
        } else if (negativeBalance) {
            isEligible = false;
            declineReason = ModifyDeclineReasons.REFUND_IN_PROGRESS;
        }
        setOccupancyStatus({ isEligible, declineReason });
    };

    const toggleModifyGuestAlertsModal = () => {
        setIsShowingModifyGuestAlertsModal(() => !isShowingModifyGuestAlertsModal);
    };

    const handleModifyGuestAlertsModalClose = () => {
        setIsShowingModifyGuestAlertsModal(() => !isShowingModifyGuestAlertsModal);
    };

    return (
        <div className="modify-guest-panel">
            <Row>
                <Col className="col-12 col-md-6">
                    <div tabIndex={0} className="info-modify-guest">
                        <IconPeopleTwo className="info-modify-guest-icon" />

                        <span className="body-medium number-guest">
                            {currentGuests} {currentGuests === 1 ? TranslationUtils.get('guest') : TranslationUtils.get('guests')}
                        </span>
                        {allowsPets ? (
                            <span className="body-medium  number-guest">
                                | {current_counts.pets}{' '}
                                {current_counts.pets === 1 ? TranslationUtils.get('pet') : TranslationUtils.get('pets')}
                            </span>
                        ) : null}
                    </div>
                    <div className="interline-number-guest-note" />
                    <span tabIndex={0} className="body-small number-guest-note">
                        {TranslationUtils.get('max_occupancy')}
                        {`: ${max_counts.total_people}`}{' '}
                        {TranslationUtils.get(max_counts.total_people === 1 ? 'guest' : 'guests').toLowerCase()}
                    </span>
                    <div className="interline-number-guest-note" />
                    {!occupancyStatus.isEligible ? (
                        <Button
                            id="button-modify-guest-alert-modal"
                            className="body-regular col-12"
                            variant="info"
                            label={TranslationUtils.get('add_remove_guest')}
                            onClick={() => toggleModifyGuestAlertsModal()}
                        />
                    ) : (
                        <Button
                            id="button-modify-your-trip"
                            className="col-12"
                            variant="info"
                            label={TranslationUtils.get('add_remove_guest')}
                            onClick={() => history.push(AppRoutes.MODIFY_GUESTS)}
                        />
                    )}
                </Col>
                <Col className="col-md-6">
                    {allowsPets ? (
                        <ImageModifyGuestWithPet className="image-modify-guest" />
                    ) : (
                        <ImageModifyGuestWithNoPet className="image-modify-guest" />
                    )}
                </Col>
            </Row>
            <ModifyGuestAlertsModal
                declineReason={occupancyStatus.declineReason}
                show={isShowingModifyGuestAlertsModal}
                handleClose={handleModifyGuestAlertsModalClose}
                phone={localPhoneNumber}
            />
        </div>
    );
};
